/**
 * 登录 / 注册页 —— 🔴 **两种形态一页**（注册即建账本，是同一件事的两半）
 *
 * ## 三条刻意的决定
 *
 * 1. **注册与登录同页切换**，不做两个路由 —— 新用户第一次来必然落在这里，
 *    分成两个页面会让人找不到"我没有账号"的入口（P0 走查项）。
 * 2. 🔴 **登录后必须自己取账本** —— `POST /auth/login` 的响应**没有** `ledgerId`
 *    （`contract/auth.ts` 明文："当前账本"是请求级上下文，不该由登录响应替客户端决定）。
 *    ⇒ 登录成功后再取 `GET /ledgers` 的第一本；而**注册**的响应**带** `ledgerId`（省一次往返）。
 * 3. **错误原样展示服务端的 `message`** —— 不自己编文案（编文案 = 把服务端口径又写一遍，会漂移）。
 */

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { deletionDaysLeft } from '@jiaying/shared';
import { useApp } from '../app/store.js';
import { ApiError } from '../api/http.js';
import { cancelDeletion, fetchLedgerListWithToken, login, register } from '../api/endpoints.js';

type Mode = 'login' | 'register';

export function LoginPage() {
  const { login: setSession } = useApp();
  const [mode, setMode] = useState<Mode>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * 🆕 **冷静期态**（`AC-N4.31` 的 UI 半边）：登录被 403 且原因是 `pending_deletion` 时置上。
   *
   * 🔴 本页是"被冻结的人"**唯一**能看到剩余天数与撤销入口的地方 —— 此刻他没有可用 token
   * （`AuthGuard` 对 `pending_deletion` 一律 403）⇒ 这些信息只可能来自**登录失败的响应体**
   * （`details.deletionDeadlineAt`）。
   */
  const [cooling, setCooling] = useState<{ deadlineAt: number | null } | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelOk, setCancelOk] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', ledgerName: '' });

  const patch = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'register') {
        // 🔴 注册响应直接带回新账本 id（省一次往返）
        const res = await register({
          email: form.email,
          password: form.password,
          name: form.name,
          ledgerName: form.ledgerName === '' ? `${form.name}的家庭` : form.ledgerName,
        });
        setSession({
          token: res.token,
          ledgerId: res.ledgerId,
          // 🆕 批次 `66`：会话补 `userId`（`AuthUserView.id` 契约里一直有 ⇒ 这是**搬运**不是新建）。
          //    实时提示条靠它排除自己触发的操作（见 `api/http.ts` 的 `Session.userId`）。
          userId: res.user.id,
          userName: res.user.name,
          ledgerName: form.ledgerName === '' ? `${form.name}的家庭` : form.ledgerName,
        });
      } else {
        const res = await login({ email: form.email, password: form.password });
        // 🔴 登录响应没有 ledgerId ⇒ 自己取第一本（见文件头决定 2）。
        //    ⚠️ 此刻 `configureApi` 的 getSession 还是**旧的 null**（会话还没写入）⇒
        //    `listLedgers()` 会带不上 Authorization。所以这里**显式**传 token 调一次原生 fetch 等价物：
        //    用 `api` 的话必须先把 token 落到会话里 —— 那就得先 setSession 再查，
        //    会多一次"账本名未知"的中间渲染。权衡后选**直接带 token 取列表**。
        const ledgers = await fetchLedgerListWithToken(res.token);
        const first = ledgers[0];
        if (first === undefined) {
          throw new ApiError(500, { message: '账号下没有任何账本 —— 这不该发生，请反馈' });
        }
        setSession({
          token: res.token,
          ledgerId: first.id,
          userId: res.user.id,
          userName: res.user.name,
          ledgerName: first.name,
        });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      // 🔴 冷静期：把截止时刻留下来（`AC-N4.31`）—— 本页"剩余 X 天 + 撤销"的唯一数据源
      //    ⚠️ `details` 是**嵌套**的（统一错误体 `{ code, message, details? }`）——
      //    服务端 `deletionDeadlineAt` 在 `details` 里，不在顶层（同 `AccountsPage` 的读法）。
      const details =
        err instanceof ApiError
          ? (err.body.details as { accountStatus?: unknown; deletionDeadlineAt?: unknown } | undefined)
          : undefined;
      if (err instanceof ApiError && err.status === 403 && details?.accountStatus === 'pending_deletion') {
        const at = details.deletionDeadlineAt;
        setCooling({ deadlineAt: typeof at === 'number' ? at : null });
      } else {
        setCooling(null);
      }
    } finally {
      setBusy(false);
    }
  }

  /**
   * 冷静期内**撤销注销**（`AC-N4.27`）—— 🔴 直接复用表单里已填的邮箱与密码：
   * 撤销接口是 `@Public()`（申请人已无可用 token），但它**要求重新证明身份**，
   * 而这两个值恰好就在眼前 ⇒ 不值得再做一个只填一次的表单。
   */
  async function undoDeletion() {
    setCancelBusy(true);
    setCancelOk(false);
    setError(null);
    try {
      await cancelDeletion(form.email, form.password);
      setCooling(null);
      setCancelOk(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setCancelBusy(false);
    }
  }

  return (
    <Box
      data-testid="login-page"
      sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2, bgcolor: 'background.default' }}
    >
      <Card sx={{ width: '100%', maxWidth: 420 }}>
        <CardContent>
          <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
            家盈
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            一个账本，全家共用
          </Typography>

          <Tabs value={mode} onChange={(_e, v: Mode) => { setMode(v); setError(null); }} sx={{ mb: 2 }}>
            <Tab value="login" label="登录" />
            <Tab value="register" label="注册" />
          </Tabs>

          <form onSubmit={submit}>
            <Stack spacing={2}>
              <TextField
                label="邮箱"
                type="email"
                required
                autoComplete="username"
                value={form.email}
                onChange={patch('email')}
              />
              <TextField
                label="密码"
                type="password"
                required
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                helperText={mode === 'register' ? '至少 8 位' : undefined}
                value={form.password}
                onChange={patch('password')}
              />
              {mode === 'register' && (
                <>
                  <TextField label="你的名字" required value={form.name} onChange={patch('name')} />
                  <TextField
                    label="账本名称"
                    placeholder="留空则用「XX的家庭」"
                    value={form.ledgerName}
                    onChange={patch('ledgerName')}
                  />
                </>
              )}

              {error !== null && <Alert severity="error">{error}</Alert>}

              {/*
                🆕 🔴 **冷静期提示 + 撤销入口**（`AC-N4.27` / `N4.31`）——
                这是被冻结的人**唯一**能看见"还剩几天"和"怎么反悔"的地方。
                剩余天数由共享层 `deletionDaysLeft()` 算（🔴 端侧**不自己除** —— 两端各算一次可能差一天）。
              */}
              {cooling !== null && (
                <Alert severity="warning" data-testid="cooling-notice">
                  <Typography variant="body2" data-testid="cooling-days">
                    {cooling.deadlineAt === null
                      ? '账号处于注销冷静期（已冻结）。'
                      : `冷静期剩余 ${String(deletionDaysLeft(cooling.deadlineAt, Date.now()))} 天，期满后账号与全部数据会被删除。`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    想反悔？用上面填的邮箱与密码直接撤销即可（撤销后立即解冻）。
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    disabled={cancelBusy || form.email === '' || form.password === ''}
                    data-testid="cancel-deletion"
                    onClick={() => {
                      void undoDeletion();
                    }}
                  >
                    {cancelBusy ? '处理中…' : '撤销注销'}
                  </Button>
                </Alert>
              )}
              {cancelOk && (
                <Alert severity="success" data-testid="cancel-ok">
                  已撤销注销 —— 账号已解冻，现在可以正常登录了。
                </Alert>
              )}

              <Button type="submit" variant="contained" size="large" disabled={busy}>
                {busy ? '处理中…' : mode === 'login' ? '登录' : '注册并开始'}
              </Button>
            </Stack>
          </form>

          {mode === 'register' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              注册会自动建好 12 个起始账户（3 个日常 + 9 个社保），社保账户默认收起 —— 用到再展开。
            </Typography>
          )}
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            <Link href="#" onClick={(e) => { e.preventDefault(); setMode(mode === 'login' ? 'register' : 'login'); }}>
              {mode === 'login' ? '还没有账号？去注册' : '已有账号？去登录'}
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
